import { TypedComponent } from '@trarc/pc-shared';
import React, { createContext, useContext, useReducer } from 'react';

import {
  RiskRegistryContextState,
  RiskRegistryDetailsContextType,
  RiskRegistryDispatch,
  RiskRegistryDispatchContextType,
  RiskRegistryDispatchHook,
  RiskRegistryDispatchTypes,
  RiskRegistryProviderProps,
  RiskRegistryReducerFunction,
  RiskRegistryStateHook,
} from './RiskRegistryContext.types';

// #START Setup
const initialState: RiskRegistryContextState = {
  filters: {
    organizationIds: [],
    rating: null,
    functionCategory: null,
    createdAt: null,
    active: false,
    accepted: false,
    resolved: false,
  },
  hasAnimatedIn: false,
  isExpandedViewModalOpen: false,
  isItemViewOpen: false,
  loading: false,
  selectedOrganizationIds: [],
  registeredRisks: [],
  showNewRiskForm: false,
  selectedRiskRecord: null,
};

/**
 * Sets up the RiskRegistry details context and defaults it to undefined so that if any RiskRegistry details context hook is used outside of the
 * RiskRegistry details context, we can throw an error.
 */
export const RiskRegistryDetailsContext: RiskRegistryDetailsContextType =
  createContext<RiskRegistryContextState>(undefined);

/**
 * Sets up the RiskRegistry dispatch context and defaults it to undefined so that if any RiskRegistry hook that uses the dispatch context is
 * used outside of the RiskRegistry dispatch context, we can throw an error.
 */
const RiskRegistryDispatchContext: RiskRegistryDispatchContextType = createContext<RiskRegistryDispatch>(undefined);

// #END Setup

// #START Hooks
/**
 * RiskRegistry context hook that retrieves the state from the context, or throws
 * an error if not used in a component that is a child of the RiskRegistry context.
 */
export const useRiskRegistryDetailsContext: RiskRegistryStateHook = () => {
  const context = useContext(RiskRegistryDetailsContext);
  if (context === undefined) {
    throw new Error('useRiskRegistryDetailsContext must be used within a RiskRegistryDetailsProvider');
  }
  return context;
};

/**
 * RiskRegistry context hook that retrieves the state from the context, or throws
 * an error if not used in a component that is a child of the RiskRegistry context.
 */
export const useRiskRegistryDispatchContext: RiskRegistryDispatchHook = () => {
  const context = useContext(RiskRegistryDispatchContext);
  if (context === undefined) {
    throw new Error('useRiskRegistryDispatch must be used within a RiskRegistryProvider');
  }
  return {
    riskRegistryDispatch: context,
  };
};

// #END Hooks

// #START Provider
/**
 * Provides the RiskRegistry state and a dispatch to update the state to all components that
 * are rendered as a child node of this component.
 *
 * @param children - {@link RiskRegistryProviderProps children} description.
 */
export const RiskRegistryDetailsProvider: TypedComponent<RiskRegistryProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(RiskRegistryReducer, initialState);

  const contextValues: RiskRegistryContextState = {
    ...state,
  };

  return (
    <RiskRegistryDetailsContext.Provider value={contextValues}>
      <RiskRegistryDispatchContext.Provider value={dispatch}>{children}</RiskRegistryDispatchContext.Provider>
    </RiskRegistryDetailsContext.Provider>
  );
};

// #END Provider

// #START Reducer
/**
 * Handles a single action dispatch to the RiskRegistry context to update context's internal state
 *
 * @param state - {@link RiskRegistryReducerFunction state} description.
 * @param action - {@link RiskRegistryReducerFunction action} description.
 */
export const RiskRegistryReducer: RiskRegistryReducerFunction = (state, action) => {
  switch (action.type) {
    case RiskRegistryDispatchTypes.setFilters:
      return {
        ...state,
        filters: action.payload,
      };
    case RiskRegistryDispatchTypes.resetFilters:
      return {
        ...state,
        filters: {
          ...initialState.filters,
          organizationIds: state.selectedOrganizationIds,
          active: true,
        },
      };
    case RiskRegistryDispatchTypes.setPanelView:
      if (!state.hasAnimatedIn) {
        return {
          ...state,
          isItemViewOpen: true,
          hasAnimatedIn: true,
        };
      } else {
        return {
          ...state,
          isItemViewOpen: true,
        };
      }
    case RiskRegistryDispatchTypes.resetPanelView:
      return {
        ...state,
        hasAnimatedIn: false,
        isItemViewOpen: false,
        showNewRiskForm: false,
        selectedRiskRecord: null,
      };
    case RiskRegistryDispatchTypes.setRegisteredRisks:
      return {
        ...state,
        registeredRisks: action.payload,
      };
    case RiskRegistryDispatchTypes.setSelectedOrganizationIds:
      return {
        ...state,
        selectedOrganizationIds: action.payload,
      };
    case RiskRegistryDispatchTypes.setSelectedRiskRecord:
      return {
        ...state,
        selectedRiskRecord: action.payload,
      };
    case RiskRegistryDispatchTypes.toggleExpandedViewModal:
      return {
        ...state,
        isExpandedViewModalOpen: !state.isExpandedViewModalOpen,
      };
    case RiskRegistryDispatchTypes.toggleShowNewRiskForm:
      return {
        ...state,
        showNewRiskForm: !state.showNewRiskForm,
      };
    default:
      return state;
  }
};

// #END Reducer
